<!-- File: frontend/src/components/List.vue -->
<template>
    <div>
        <confirm ref="confirm"></confirm>

        <!-- Loading Overlay for Bulk Delete -->
        <v-overlay :value="bulkDeleteLoading" z-index="9999">
            <v-card color="white" class="pa-6" elevation="10">
                <v-card-title class="justify-center">
                    <v-icon large color="primary" class="mr-2">mdi-delete-sweep</v-icon>
                    {{ $t('deleting items') }}
                </v-card-title>
                <v-card-text class="text-center">
                    <v-progress-circular
                        :size="70"
                        :width="7"
                        color="primary"
                        indeterminate
                        class="mb-4"
                    ></v-progress-circular>
                    <div class="text-h6">{{ bulkDeleteProgress }}</div>
                    <div class="mt-2">{{ bulkDeleteStatus }}</div>
                </v-card-text>
            </v-card>
        </v-overlay>

        <!-- Error Alert -->
        <v-alert
            v-if="errorMessage"
            type="error"
            dismissible
            @input="errorMessage = ''"
            class="mb-4"
        >
            <strong>{{ $t('error') }}:</strong> {{ errorMessage }}
        </v-alert>

        <v-card>
            <v-card-title>{{ label }}</v-card-title>

            <v-card-text>

                <!-- Fullscreen image dialog -->
                <v-dialog v-model="imageDialog" max-width="95vw">
                    <v-card>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn icon @click="imageDialog = false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-card-actions>

                        <v-card-text style="display:flex;justify-content:center;">
                            <img
                                v-if="imageDialogSrc"
                                :src="imageDialogSrc"
                                style="max-width:90vw;max-height:85vh;object-fit:contain;"
                                alt="Full image"
                            />
                        </v-card-text>
                    </v-card>
                </v-dialog>

                <v-data-table
                    :headers="filteredHeader"
                    :items="result"
                    :items-per-page="10"
                    :footer-props="{
                        'items-per-page-text': this.$t('records per page'),
                        'show-current-page': true,
                        'show-first-last-page': true,
                        'items-per-page-options': [100, 1000]
                    }"
                    :no-data-text="$t('no records found')"
                    :no-results-text="$t('no records found')"
                    :header-props="{ 'sort-by-text': $t('sort by') }"
                    :loading-text="$t('loading')"
                    :options.sync="options"
                    :page.sync="page"
                    :server-items-length="totalRows"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    :must-sort="true"
                    :loading="loading"
                    show-select
                    v-model="selected"
                    @dblclick:row="rowDblClicked"
                    @click:row="rowClicked"
                    :item-class="computeClass"
                    single-expand
                    :expanded.sync="expanded"
                    item-key="id"
                >

                    <!-- BOOLEAN COLUMNS: explicit slots for known PZN boolean fields -->
                    <template v-slot:item.prioproduct="{ item }">
                        <v-icon v-if="item.prioproduct" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.launch="{ item }">
                        <v-icon v-if="item.launch" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.zalando="{ item }">
                        <v-icon v-if="item.zalando" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.douglas="{ item }">
                        <v-icon v-if="item.douglas" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.defektmonitor="{ item }">
                        <v-icon v-if="item.defektmonitor" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.isadmin="{ item }">
                        <v-icon v-if="item.isadmin" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.isactive="{ item }">
                        <v-icon v-if="item.isactive" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.processed="{ item }">
                        <v-icon v-if="item.processed" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.relevant="{ item }">
                        <v-icon v-if="item.relevant" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.slide="{ item }">
                        <v-icon v-if="item.slide" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.recommended="{ item }">
                        <v-icon v-if="item.recommended" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.available="{ item }">
                        <v-icon v-if="item.available" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.picavailable="{ item }">
                        <v-icon v-if="item.picavailable" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.vidavailable="{ item }">
                        <v-icon v-if="item.vidavailable" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.pdfavailable="{ item }">
                        <v-icon v-if="item.pdfavailable" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.hastext="{ item }">
                        <v-icon v-if="item.hastext" color="success" small>mdi-check-bold</v-icon>
                    </template>
                    <template v-slot:item.need_screenshot="{ item }">
                        <v-icon v-if="item.need_screenshot" color="success" small>mdi-check-bold</v-icon>
                    </template>

                    <!-- ACTIONS HEADER -->
                    <template v-slot:header.actions>
                        <v-menu bottom offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="#49a5ac"
                                    fab
                                    x-small
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-menu</v-icon>
                                </v-btn>
                            </template>

                            <v-list dense rounded>
                                <v-list-item v-if="!hideNewAndEdit" @click="addNew()">
                                    <v-list-item-icon><v-icon dense>mdi-plus-circle-outline</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('new') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="refreshList()">
                                    <v-list-item-icon><v-icon dense>mdi-refresh</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('refresh') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="!hideExport" @click="exportAll()">
                                    <v-list-item-icon><v-icon dense>mdi-file-delimited-outline</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('export all') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="!hideExport" @click="exportPage()">
                                    <v-list-item-icon><v-icon dense>mdi-file-delimited-outline</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('export page') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="!hideDelete && selected.length > 0" @click="bulkDelete()">
                                    <v-list-item-icon><v-icon dense>mdi-delete-outline</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('delete selected') }} ({{ selected.length }})</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                    <!-- SEARCH -->
                    <template v-slot:top>
                        <v-text-field
                            v-model="search"
                            append-outer-icon="mdi-magnify"
                            clearable
                            :label="$t('search')"
                            dense
                            @click:append-outer="filterData"
                            @keydown.enter="filterData"
                        />
                    </template>

                    <!-- Expanded detail / sublists (restored from list_old) -->
                  <template v-slot:item.actions="{ item }">
                      <v-icon :title="$t('edit')" v-if="!hideNewAndEdit && !showDownload" dense class="mr-2" @click="editItem(item)">mdi-pencil-outline</v-icon>
                      <v-icon :title="$t('download')" v-if="showDownload" dense class="mr-2" @click="editItem(item)">mdi-file-download-outline</v-icon>
                      <v-icon :title="$t('delete')" v-if="!hideDelete" dense class="mr-2" @click="deleteItem(item)">mdi-delete-outline</v-icon>
                  </template>
                    <template v-slot:expanded-item="{ headers, item }" v-if="subListDefinition && subListDefinition.length > 0">
                        <td :colspan="headers.length" style="padding-top:10px;padding-bottom:10px;">
                            <v-sheet elevation="6">
                                <v-tabs background-color="#57ACB3" dark next-icon="mdi-arrow-right-bold-box-outline" prev-icon="mdi-arrow-left-bold-box-outline" v-model="tabs">
                                    <v-tabs-slider></v-tabs-slider>
                                    <v-tab v-for="subList in subListDefinition" :key="subList.label">{{ subList.label }}</v-tab>
                                </v-tabs>
                                <v-tabs-items v-model="tabs">
                                    <v-tab-item v-for="subList in subListDefinition" :key="subList.label">
                                        <sublist
                                            :type="subList.type"
                                            :label="subList.label"
                                            :table="subList.table"
                                            :initialSortBy="subList.initialSortBy"
                                            :initialSortDesc="subList.initialSortDesc"
                                            :parentId="item.id"
                                            v-on:childMessage="onMessageFromSubListReceived"
                                            :refresh="refreshSubList"
                                        ></sublist>
                                    </v-tab-item>
                                </v-tabs-items>
                            </v-sheet>
                        </td>
                    </template>

                    <!-- ✅ SOURCE COLUMN: per-row logic (keeps previous src handling) -->
                    <template v-slot:item.src="{ item }">
                        <img
                            v-if="item && item.img_url"
                            :src="item.img_url"
                            @click="openImage(item.img_url)"
                            style="max-width:200px;max-height:200px;object-fit:contain;border-radius:6px;cursor:pointer;display:block;"
                            alt="Image preview"
                        >
                        <img
                            v-else
                            height="50"
                            :src="generateDownloadUrl(item)"
                            alt="Source"
                        >
                    </template>

                    <!-- Render `img_url` as small (25\% size) clickable image and label it Source -->
                    <template v-slot:item.img_url="{ item }">
                        <img
                            v-if="item && item.img_url"
                            :src="item.img_url"
                            @click="openImage(item.img_url)"
                            style="max-width:200px;max-height:200px;object-fit:contain;border-radius:6px;cursor:pointer;display:block;"
                            alt="Source preview"
                        />
                    </template>

                    <!-- LOCAL FILE -->
                    <template v-slot:item.local_file="{ item }">
                        <img height="50" :src="generateDownloadCategoryAdUrl(item)">
                    </template>

                    <!-- FOOTER -->
                    <template v-slot:footer.page-text="props">
                        {{ props.pageStart }} - {{ props.pageStop }} {{ $t('of') }} {{ props.itemsLength }}
                    </template>

                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import Api from "@/Api";
import Confirm from "@/components/Confirm";

export default {
    components: {
        Confirm,
        Sublist: () => import("@/components/Sublist.vue")
    },

    props: [
        "table",
        "refresh",
        "hideNewAndEdit",
        "hideDelete",
        "hideExport",
        "showDownload",
        "initialSortBy",
        "initialSortDesc",
        "parentId",
        "label",
        "sublist",
        "refreshSubList",
        "subListDefinition"
    ],

    computed: {
      filteredHeader() {
          let h;
          if (this.table === "customers" || this.table === "scraper" || this.table === "pharmacies")
              h = this.header;
          else
              h = this.header.filter(h => h.value !== "id");

          // remove source-related columns
          h = h.filter(col =>
              col.value !== "src" &&
              col.value !== "source" &&
              col.value !== "image_src"
          );

          for (let item of h) {
              item.text = this.$t(item.text);
          }
          return h;
      }
    },

    data() {
        return {
            result: [],
            header: [],
            search: "",
            options: {},
            totalRows: 0,
            pageSize: 10,
            page: 1,
            sortBy: this.initialSortBy,
            sortDesc: this.initialSortDesc,
            loading: false,
            selectedId: null,
            expanded: [],
            selected: [],
            errorMessage: "",

            // Bulk delete loading states
            bulkDeleteLoading: false,
            bulkDeleteProgress: "",
            bulkDeleteStatus: "",

            // ✅ added for fullscreen preview
            imageDialog: false,
            imageDialogSrc: "",

            // tabs for sublists
            tabs: ""
        };
    },

    watch:
    {
        options:
        {
            handler (options)
            {
                this.sortBy = options.sortBy[0];
                this.sortDesc = options.sortDesc[0];
                this.pageSize = options.itemsPerPage;
                this.getData(options.page, options.itemsPerPage, this.sortBy, this.sortDesc, this.parentId);
            },
            deep: true,
        },
        refresh: function()
        {
            this.getData(1, this.pageSize, this.sortBy, this.sortDesc, this.parentId);
            this.page = 1;
        },
        parentId: function()
        {
            this.getData(1, this.pageSize, this.sortBy, this.sortDesc, this.parentId);
            this.page = 1;
        }
    },

    mounted() {
        // Initialize data on mount to ensure buttons work even with empty list
        this.getData(1, this.pageSize, this.sortBy, this.sortDesc, this.parentId);
    },

    methods: {
        // ✅ added for fullscreen preview
        openImage(url) {
            this.imageDialogSrc = url;
            this.imageDialog = true;
        },

        onMessageFromSubListReceived (message) {
            this.$emit("sublistMessage", message);
        },

        computeClass(item) {
            if (this.selectedId != null && item.id === this.selectedId)
                return "green lighten-5";
        },
        generateDownloadUrl(item) {
            return Api.generateDownloadAdUrl(item.name, item.src);
        },
        generateDownloadCategoryAdUrl(item) {
            return Api.generateDownloadCategoryAdUrl(item.name, item.local_file);
        },
        refreshList() {
            this.getData(1, this.pageSize, this.sortBy, this.sortDesc, this.parentId);
            this.page = 1;
        },
        addNew() {
            // include header like in list_old
            this.$emit("childMessage", { cmd: "newRecord", header: this.header });
        },
        rowDblClicked(ev, data) {
            this.editItem(data.item);
        },
        rowClicked(item, data) {
            // Toggle: if already expanded, collapse; otherwise expand
            if (this.expanded.length > 0 && this.expanded[0].id === data.item.id) {
                this.expanded = [];
                this.selectedId = null;
            } else {
                this.expanded = [data.item];
                this.selectedId = data.item.id;
                this.$emit("childMessage", { cmd: "recordSelected", header: this.header, item: data.item });
            }
        },
        editItem(item) {
            // include header like in list_old
            this.$emit("childMessage", { cmd: "updRecord", header: this.header, item });
        },
        deleteItem(item) {
            // restore confirm/delete behavior if needed
            this.$refs.confirm.open(this.$t('Wirklich löschen?') || 'Wirklich löschen?').then((confirm) =>
            {
                if (confirm)
                {
                    if (this.expanded.length > 0 && this.expanded[0].id == item.id)
                        this.expanded = [];
                    this.$emit("childMessage", { cmd: "delRecord", item: item });
                }
            });
        },
        async bulkDelete() {
            const itemsToDelete = [...this.selected];
            const totalItems = itemsToDelete.length;

            if (totalItems === 0) {
                return;
            }

            // Ask for confirmation
            const confirmMessage = this.$t('delete selected items confirmation') ||
                                 `Wirklich ${totalItems} Einträge löschen?`;
            const confirmed = await this.$refs.confirm.open(confirmMessage);

            if (!confirmed) {
                return;
            }

            // Start bulk delete process
            this.bulkDeleteLoading = true;
            this.bulkDeleteProgress = `0 / ${totalItems}`;
            this.bulkDeleteStatus = this.$t('preparing deletion') || 'Vorbereitung...';

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Loop through selected items and delete one by one
            for (let i = 0; i < itemsToDelete.length; i++) {
                const item = itemsToDelete[i];
                this.bulkDeleteProgress = `${i + 1} / ${totalItems}`;
                this.bulkDeleteStatus = this.$t('deleting item') || `Lösche Eintrag ${item.id}...`;

                try {
                    // Call the backend delete endpoint
                    const result = await Api.deleteRecord(this.table, item.id);

                    if (result.status === 200) {
                        successCount++;
                    } else {
                        errorCount++;
                        errors.push({ id: item.id, error: this.$t('deletion failed') || 'Löschen fehlgeschlagen' });
                    }
                } catch (e) {
                    errorCount++;
                    const errorMsg = (e.response && e.response.data && e.response.data.message) || e.message || this.$t('unknown error');
                    errors.push({ id: item.id, error: errorMsg });
                    console.error(`Error deleting item ${item.id}:`, e);
                }
            }

            // Finish deletion process
            this.bulkDeleteLoading = false;
            this.selected = [];

            // Show result message
            let message = `${this.$t('deletion complete') || 'Löschen abgeschlossen'}\n`;
            message += `${this.$t('successful') || 'Erfolgreich'}: ${successCount}\n`;

            if (errorCount > 0) {
                message += `${this.$t('failed') || 'Fehlgeschlagen'}: ${errorCount}\n\n`;
                if (errors.length > 0) {
                    message += this.$t('errors') || 'Fehler' + ':\n';
                    errors.slice(0, 5).forEach(err => {
                        message += `ID ${err.id}: ${err.error}\n`;
                    });
                    if (errors.length > 5) {
                        message += `... ${this.$t('and') || 'und'} ${errors.length - 5} ${this.$t('more') || 'weitere'}`;
                    }
                }
            }

            alert(message);

            // Refresh the list
            this.refreshList();
        },
        filterData() {
            this.expanded = [];
            this.getData(1, this.pageSize, this.sortBy, this.sortDesc, this.parentId);
            this.page = 1;
        },
        getData(page, pageSize, sortBy, sortDesc, parentId) {
            this.loading = true;
            this.errorMessage = ""; // Clear any previous errors
            console.log("getData table=", this.table, { page, pageSize, sortBy, sortDesc, parentId });
            Api.getRecords(this.table, page, pageSize, this.search, sortBy, sortDesc, parentId)
                .then(response => {
                    this.result = response.data.result.map(row => {
                        const newRow = { ...row };
                        for (const key of Object.keys(newRow)) {
                            if (newRow[key] === 'true' || newRow[key] === true) newRow[key] = true;
                            else if (newRow[key] === 'false' || newRow[key] === false) newRow[key] = false;
                        }
                        return newRow;
                    });
                    this.header = response.data.header || [];

                    // rename `img_url` column to `Source`
                    const imgHeader = this.header.find(h => h.value === "img_url");
                    if (imgHeader) imgHeader.text = "Source";

                    // Always ensure actions column is present (remove old one if exists)
                    this.header = this.header.filter(h => h.value !== "actions");
                    this.header.unshift({ text: "", width: 90, value: "actions", sortable: false });
                    this.totalRows = response.data.total;
                    this.loading = false;
                    this.selectedId = null;
                    this.selected = [];


                    // emit searchExecuted like in list_old
                    this.$emit("childMessage", { cmd: "searchExecuted" });
                    this.expanded = [];
                })
                .catch((e) => {
                    this.loading = false;
                    console.error("Error loading data:", e);

                    // Build user-friendly error message
                    let errorMsg = this.$t("error loading data");

                    if (e.response) {
                        // Server responded with an error
                        if (e.response.data && e.response.data.message) {
                            errorMsg = e.response.data.message;
                        } else if (e.response.status === 500) {
                            errorMsg = this.$t("server error") + ": " + this.$t("database schema mismatch or missing columns");
                        } else if (e.response.status === 404) {
                            errorMsg = this.$t("table not found");
                        } else if (e.response.status === 403) {
                            errorMsg = this.$t("access denied");
                        }
                    } else if (e.request) {
                        // Request was made but no response
                        errorMsg = this.$t("no response from server");
                    } else {
                        // Something else happened
                        errorMsg = e.message || this.$t("unknown error");
                    }

                    this.errorMessage = errorMsg;

                    // Ensure actions header exists even on error
                    if (this.header.length === 0 || !this.header.find(h => h.value === "actions")) {
                        this.header = this.header.filter(h => h.value !== "actions");
                        this.header.unshift({ text: "", width: 90, value: "actions", sortable: false });
                    }
                });
        },
        exportAll() {
            const self = this;
            Api.exportRecords(this.table, this.search, this.sortBy, this.sortDesc, this.parentId).then(response =>
            {
                const ticket = response.data.ticket;
                Api.exportTicket(ticket);
            })
            .catch((e) =>
            {
                let txt = self.$t("export not possible");
                alert(txt);
            });
        },
        exportPage() {
            const self = this;
            Api.exportRecords(this.table, this.search, this.sortBy, this.sortDesc, this.parentId, this.page, this.pageSize).then(response =>
            {
                const ticket = response.data.ticket;
                Api.exportTicket(ticket);
            })
            .catch((e) =>
            {
                let txt = self.$t("export not possible");
                alert(txt);
            });
        }
    }
};
</script>

<style>
tbody tr:nth-of-type(odd) {
    background-color: #e3eeee;
}

th {
    position: sticky;
    top: 3rem;
    background: lightgray;
    z-index: 6;
}

.v-data-table .v-data-table__wrapper {
    overflow: unset;
}
</style>
